import { ApiError, apiClient } from '@/lib/api-client';
import { API_ROUTES } from '@/lib/api-config';
import type { Meeting, OccupiedSlot } from '@/types/meetings.type';

export type CreateMeetingPayload = {
  prestations: Array<{
    service_id: string;
    product_id?: string;
  }>;
  start_time: Date | string;
  send_notification?: boolean;
  client: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
};

export type UpdateMeetingPayload = {
  prestations: Array<{
    service_id: string;
    product_id?: string;
  }>;
  start_time: Date | string;
  send_notification?: boolean;
  token: string;
};

export type CancelMeetingPayload = {
  send_notification?: boolean;
  token: string;
};

export type MeetingResponse = {
  success: boolean;
  data?: Meeting;
  error?: {
    message: string;
    code: string;
  };
  conflicts?: Array<{
    start_time: string;
    end_time: string;
  }>;
};

/**
 * Get all meetings from today onwards (for availability checking)
 * Uses public route - can be called client-side and server-side
 */
export const getMeetingsFromToday = async (): Promise<Array<Meeting>> => {
  return apiClient.get<Array<Meeting>>(API_ROUTES.PUBLIC_MEETINGS_FROM_TODAY);
};

/**
 * Get only occupied time slots from today onwards (optimized for availability checking)
 * Returns minimal data: only start_time and end_time for non-cancelled meetings
 * Uses public route - can be called client-side and server-side
 */
export const getOccupiedSlotsFromToday = async (): Promise<Array<OccupiedSlot>> => {
  return apiClient.get<Array<OccupiedSlot>>(API_ROUTES.PUBLIC_MEETINGS_OCCUPIED_SLOTS);
};

/**
 * Get a single meeting by ID
 * Uses public route - can be called client-side
 */
export const getMeetingById = async (id: string): Promise<Meeting> => {
  return apiClient.get<Meeting>(API_ROUTES.PUBLIC_MEETING_BY_ID(id));
};

/**
 * Create a new meeting
 * Uses public route - can be called client-side
 */
export const createMeeting = async (payload: CreateMeetingPayload): Promise<MeetingResponse> => {
  try {
    const data = await apiClient.post<Meeting>(API_ROUTES.PUBLIC_MEETINGS, payload);
    return { success: true, data };
  } catch (error) {
    if (error instanceof ApiError) {
      // Handle conflicts (409) - return response with conflicts
      if (error.status === 409) {
        return {
          success: false,
          error: { message: error.message, code: error.code || 'CONFLICT' },
          conflicts: error.meta as Array<{ start_time: string; end_time: string }>,
        };
      }
      throw error;
    }
    throw error;
  }
};

/**
 * Update an existing meeting
 * Uses public route - can be called client-side
 */
export const updateMeeting = async (
  id: string,
  payload: UpdateMeetingPayload,
): Promise<MeetingResponse> => {
  const { token, ...body } = payload;
  try {
    const data = await apiClient.patch<Meeting>(API_ROUTES.PUBLIC_MEETING_BY_ID(id), body, {
      headers: { 'X-Meeting-Token': token },
    });
    return { success: true, data };
  } catch (error) {
    if (error instanceof ApiError) {
      // Handle conflicts (409) - return response with conflicts
      if (error.status === 409) {
        return {
          success: false,
          error: { message: error.message, code: error.code || 'CONFLICT' },
          conflicts: error.meta as Array<{ start_time: string; end_time: string }>,
        };
      }
      throw error;
    }
    throw error;
  }
};

/**
 * Cancel a meeting
 * Uses public route - can be called client-side
 */
export const cancelMeeting = async (
  id: string,
  payload: CancelMeetingPayload,
): Promise<MeetingResponse> => {
  const { token, ...rest } = payload;
  const queryParams = new URLSearchParams();
  if (rest.send_notification !== undefined) {
    queryParams.append('send_notification', rest.send_notification.toString());
  }

  const url = `${API_ROUTES.PUBLIC_MEETING_CANCEL(id)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const data = await apiClient.patch<Meeting>(url, undefined, {
    headers: { 'X-Meeting-Token': token },
  });
  return { success: true, data };
};
